import { forwardRef, HTMLAttributes } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  value: number; // 0-100
  max?: number;
  variant?: 'default' | 'xp' | 'streak' | 'accuracy';
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  label?: string;
  animate?: boolean;
}

const sizeStyles = {
  sm: 'h-1.5',
  md: 'h-2.5',
  lg: 'h-4',
};

const variantStyles = {
  default: 'bg-primary-400',
  xp: 'bg-gradient-to-r from-primary-400 to-mint-200',
  streak: 'bg-gradient-to-r from-coral-300 to-coral-400',
  accuracy: 'bg-gradient-to-r from-mint-200 to-primary-400',
};

export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      value,
      max = 100,
      variant = 'default',
      size = 'md',
      showLabel = false,
      label,
      animate = true,
      className,
      ...props
    },
    ref
  ) => {
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

    return (
      <div ref={ref} className={clsx('w-full', className)} {...props}>
        {(showLabel || label) && (
          <div className="flex justify-between items-center mb-2">
            {label && (
              <span className="text-sm font-medium text-charcoal-600">
                {label}
              </span>
            )}
            {showLabel && (
              <span className="text-sm text-charcoal-400">
                {Math.round(percentage)}%
              </span>
            )}
          </div>
        )}
        <div
          className={clsx(
            'w-full bg-cream-300 rounded-full overflow-hidden',
            sizeStyles[size]
          )}
        >
          {animate ? (
            <motion.div
              className={clsx('h-full rounded-full', variantStyles[variant])}
              initial={{ width: 0 }}
              animate={{ width: `${percentage}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          ) : (
            <div
              className={clsx('h-full rounded-full', variantStyles[variant])}
              style={{ width: `${percentage}%` }}
            />
          )}
        </div>
      </div>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';

// XP Progress with level info
interface XPProgressProps {
  currentXP: number;
  xpForNextLevel: number;
  level: number;
  levelName: string;
  className?: string;
}

export const XPProgress = forwardRef<HTMLDivElement, XPProgressProps>(
  (
    {
      currentXP,
      xpForNextLevel,
      level,
      levelName,
      className,
    },
    ref
  ) => {
    return (
      <div ref={ref} className={clsx('w-full', className)}>
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-charcoal-600">
              Level {level}
            </span>
            <span className="text-sm text-charcoal-400">{levelName}</span>
          </div>
          <span className="text-sm text-charcoal-400">
            {currentXP.toLocaleString()} / {xpForNextLevel.toLocaleString()} XP
          </span>
        </div>
        <ProgressBar
          value={currentXP}
          max={xpForNextLevel}
          variant="xp"
          size="lg"
        />
      </div>
    );
  }
);

XPProgress.displayName = 'XPProgress';

// Circular Progress
interface CircularProgressProps {
  value: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  showValue?: boolean;
  label?: string;
  variant?: 'default' | 'xp' | 'streak' | 'accuracy';
  className?: string;
}

export const CircularProgress = ({
  value,
  max = 100,
  size = 80,
  strokeWidth = 8,
  showValue = true,
  label,
  variant = 'default',
  className,
}: CircularProgressProps) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  const strokeColors = {
    default: 'stroke-primary-400',
    xp: 'stroke-primary-400',
    streak: 'stroke-coral-300',
    accuracy: 'stroke-mint-200',
  };

  return (
    <div className={clsx('inline-flex flex-col items-center', className)}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          className="stroke-cream-300"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          className={strokeColors[variant]}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          style={{
            strokeDasharray: circumference,
          }}
        />
      </svg>
      {showValue && (
        <div
          className="absolute flex flex-col items-center justify-center"
          style={{ width: size, height: size }}
        >
          <span className="text-xl font-bold text-charcoal-600">
            {Math.round(percentage)}%
          </span>
          {label && (
            <span className="text-xs text-charcoal-400">{label}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgressBar;
